/**
 * useRouteMonitor - Hook para monitoramento de rotas em tempo real
 *
 * Integra o RouteMonitor com o Redux store do Traccar para receber
 * atualizacoes de posicao automaticamente via WebSocket
 *
 * FASE 2: Agora persiste estado no backend para permitir:
 * - Recuperar estado ao reabrir
 * - Sincronizar entre dispositivos
 * - Continuar de onde parou
 */

import {
  useState, useEffect, useCallback, useRef, useMemo,
} from 'react';
import { useSelector } from 'react-redux';
import {
  RouteMonitor,
  EXECUTION_STATUS,
  calculateRouteProgress,
  checkRouteDeviation,
  calculateETA,
} from '../services/routeMonitoringService';
import routeExecutionService, { EXECUTION_STATE } from '../services/routeExecutionService';
import { reportDeviation, reportDelay, ALERT_TYPES } from '../services/routeAlertService';

/**
 * Mapeia status interno para status de persistencia
 * (reservado para uso futuro em sincronizacao)
 */
const _mapStatusToExecutionState = (status) => {
  switch (status) {
    case EXECUTION_STATUS.IN_PROGRESS:
      return EXECUTION_STATE.IN_PROGRESS;
    case EXECUTION_STATUS.PAUSED:
      return EXECUTION_STATE.PAUSED;
    case EXECUTION_STATUS.COMPLETED:
      return EXECUTION_STATE.COMPLETED;
    case EXECUTION_STATUS.CANCELLED:
      return EXECUTION_STATE.CANCELLED;
    default:
      return EXECUTION_STATE.NOT_STARTED;
  }
};

/**
 * Mapeia status de persistencia para status interno
 */
const mapExecutionStateToStatus = (state) => {
  switch (state) {
    case EXECUTION_STATE.IN_PROGRESS:
      return EXECUTION_STATUS.IN_PROGRESS;
    case EXECUTION_STATE.PAUSED:
      return EXECUTION_STATUS.PAUSED;
    case EXECUTION_STATE.COMPLETED:
      return EXECUTION_STATUS.COMPLETED;
    case EXECUTION_STATE.CANCELLED:
      return EXECUTION_STATUS.CANCELLED;
    default:
      return EXECUTION_STATUS.NOT_STARTED;
  }
};

// Track recently reported alerts to avoid duplicates
const recentAlerts = new Map();
const ALERT_DEBOUNCE_MS = 60000; // Don't report same alert type within 1 minute
const ALERT_CLEANUP_INTERVAL = 5 * 60000; // Cleanup every 5 minutes

// Periodic cleanup of expired entries to prevent memory leak
let alertCleanupTimer = null;
const startAlertCleanup = () => {
  if (alertCleanupTimer) return;
  alertCleanupTimer = setInterval(() => {
    const now = Date.now();
    recentAlerts.forEach((timestamp, key) => {
      if (now - timestamp > ALERT_DEBOUNCE_MS * 2) {
        recentAlerts.delete(key);
      }
    });
    if (recentAlerts.size === 0) {
      clearInterval(alertCleanupTimer);
      alertCleanupTimer = null;
    }
  }, ALERT_CLEANUP_INTERVAL);
};

/**
 * Report significant alerts to backend for manager notifications.
 * Debounces alerts to avoid spamming.
 */
const reportAlertToBackend = async (alert, planId, planVehicleId) => {
  const alertKey = `${planId}-${alert.type}`;

  // Check if we recently reported this alert type
  const lastReported = recentAlerts.get(alertKey);
  if (lastReported && Date.now() - lastReported < ALERT_DEBOUNCE_MS) {
    return;
  }

  recentAlerts.set(alertKey, Date.now());
  startAlertCleanup();

  try {
    if (alert.type === ALERT_TYPES.ROUTE_DEVIATION && alert.data) {
      await reportDeviation({
        routePlanId: planId,
        vehicleId: planVehicleId,
        distanceMeters: alert.data.distance || 0,
        latitude: alert.data.position?.latitude || 0,
        longitude: alert.data.position?.longitude || 0,
      });
    } else if (alert.type === ALERT_TYPES.LATE_ARRIVAL && alert.data) {
      const delayMs = alert.data.newETA - alert.data.originalETA;
      const delayMinutes = Math.round(delayMs / 60000);

      await reportDelay({
        routePlanId: planId,
        vehicleId: planVehicleId,
        stopId: alert.data.stopId,
        delayMinutes,
        originalETA: alert.data.originalETA?.toISOString?.() || null,
        newETA: alert.data.newETA?.toISOString?.() || null,
      });
    }
  } catch (error) {
    console.error('[useRouteMonitor] Failed to report alert to backend:', error);
  }
};

/**
 * Hook principal para monitoramento de rota com persistencia
 *
 * @param {Object} route - Rota otimizada a ser monitorada
 * @param {Object} vehicle - Veiculo (deve ter deviceId para buscar posicao)
 * @param {Object} options - Opcoes de configuracao
 * @param {number} options.planId - ID do plano para persistencia
 * @param {number} options.planVehicleId - ID do veiculo no plano (route_plan_vehicles.id)
 * @param {boolean} options.enablePersistence - Habilitar persistencia no backend (default: true)
 * @param {boolean} options.enableSync - Habilitar sincronizacao via polling (default: true)
 * @param {number} options.syncInterval - Intervalo de sincronizacao em ms (default: 30000)
 * @param {Object} options.initialState - Estado inicial recuperado do backend
 * @returns {Object} Estado e funcoes de controle do monitoramento
 */
const useRouteMonitor = (route, vehicle, options = {}) => {
  const {
    planId,
    planVehicleId,
    enablePersistence = true,
    enableSync = true,
    syncInterval = 30000,
    initialState = null,
  } = options;

  // Redux - posicoes em tempo real
  const positions = useSelector((state) => state.session.positions);

  // Estado do monitoramento (inicializa com estado recuperado se disponivel)
  const [status, setStatus] = useState(() => {
    if (initialState?.executionState) {
      return mapExecutionStateToStatus(initialState.executionState);
    }
    return EXECUTION_STATUS.NOT_STARTED;
  });

  const [progress, setProgress] = useState(() => initialState?.progress || null);
  const [deviation, setDeviation] = useState(null);
  const [nextStopETA, setNextStopETA] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [completedStops, setCompletedStops] = useState(() => initialState?.completedStops || []);
  const [currentPosition, setCurrentPosition] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [syncError, setSyncError] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);

  // Refs
  const monitorRef = useRef(null);
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const syncStopRef = useRef(null);
  const deviceId = vehicle?.id || vehicle?.deviceId;

  // Posicao atual do veiculo (do Redux)
  const vehiclePosition = useMemo(() => {
    if (!deviceId || !positions) return null;
    return positions[deviceId] || null;
  }, [positions, deviceId]);

  // Inicializar o monitor
  // options is stored in a ref to prevent re-triggering this effect
  // (callers typically pass an inline object literal, creating new refs each render)
  useEffect(() => {
    if (!route || !vehicle) return;

    monitorRef.current = new RouteMonitor(route, vehicle, optionsRef.current);

    // Configurar listeners
    monitorRef.current.on('update', (update) => {
      setProgress(update.progress);
      setDeviation(update.deviation);
      setNextStopETA(update.nextStopETA);
      setAlerts(update.alerts || []);
      setLastUpdate(update.timestamp);
    });

    monitorRef.current.on('alert', (alert) => {
      setAlerts((prev) => [...prev, alert]);

      // Report significant alerts to backend for manager notifications
      if (planId && enablePersistence && (alert.severity === 'warning' || alert.severity === 'error')) {
        reportAlertToBackend(alert, planId, planVehicleId);
      }
    });

    monitorRef.current.on('started', () => {
      setStatus(EXECUTION_STATUS.IN_PROGRESS);
    });

    monitorRef.current.on('completed', () => {
      setStatus(EXECUTION_STATUS.COMPLETED);
    });

    monitorRef.current.on('paused', () => {
      setStatus(EXECUTION_STATUS.PAUSED);
    });

    monitorRef.current.on('resumed', () => {
      setStatus(EXECUTION_STATUS.IN_PROGRESS);
    });

    monitorRef.current.on('stopCompleted', ({ stop }) => {
      setCompletedStops((prev) => [...prev, stop.id]);
    });

    // Se houver estado inicial em progresso, configurar o monitor
    const currentInitialState = optionsRef.current.initialState || null;
    if (currentInitialState?.executionState === EXECUTION_STATE.IN_PROGRESS) {
      // Marcar paradas ja concluidas no monitor
      if (currentInitialState.completedStops?.length > 0) {
        currentInitialState.completedStops.forEach((stopId) => {
          monitorRef.current.completeStop(stopId, '(recuperado)');
        });
      }
    }

    return () => {
      monitorRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route, vehicle]);

  // Recover status from late-arriving initialState (loaded async after first render)
  useEffect(() => {
    if (!initialState?.executionState) return;
    setStatus((current) => {
      if (current === EXECUTION_STATUS.NOT_STARTED) {
        return mapExecutionStateToStatus(initialState.executionState);
      }
      return current;
    });
    if (initialState.completedStops?.length > 0) {
      setCompletedStops(initialState.completedStops);
    }
    if (initialState.progress) {
      setProgress(initialState.progress);
    }
  }, [initialState]);

  // Iniciar polling de sincronizacao
  useEffect(() => {
    if (!enableSync || !planId || status === EXECUTION_STATUS.COMPLETED) {
      return undefined;
    }

    // Iniciar polling apenas se em execucao ou pausado
    if (status === EXECUTION_STATUS.IN_PROGRESS || status === EXECUTION_STATUS.PAUSED) {
      syncStopRef.current = routeExecutionService.startSyncPolling(
        planId,
        (updates) => {
          // Atualizar estado se houver mudancas externas
          if (updates.state) {
            const newStatus = mapExecutionStateToStatus(updates.state.executionState);
            if (newStatus !== status) {
              setStatus(newStatus);
            }

            // Atualizar paradas concluidas
            if (updates.state.completedStops) {
              setCompletedStops(updates.state.completedStops);
            }

            // Atualizar progresso
            if (updates.state.progress) {
              setProgress(updates.state.progress);
            }
          }

          // Processar novos eventos
          if (updates.events?.length > 0) {
            updates.events.forEach((event) => {
              if (event.severity === 'warning' || event.severity === 'error') {
                setAlerts((prev) => [
                  ...prev,
                  {
                    id: event.id,
                    type: event.type,
                    message: event.message,
                    severity: event.severity,
                    timestamp: event.createdAt,
                  },
                ]);
              }
            });
          }

          setLastUpdate(updates.syncTime);
        },
        syncInterval
      );
    }

    return () => {
      if (syncStopRef.current) {
        syncStopRef.current();
        syncStopRef.current = null;
      }
    };
  }, [enableSync, planId, status, syncInterval]);

  // Atualizar posicao quando mudar no Redux (via WebSocket)
  useEffect(() => {
    if (!vehiclePosition || status !== EXECUTION_STATUS.IN_PROGRESS) return;

    setCurrentPosition(vehiclePosition);

    // Atualizar o monitor com a nova posicao
    if (monitorRef.current) {
      monitorRef.current.updatePosition(vehiclePosition);
    }
  }, [vehiclePosition, status]);

  // ============================================================================
  // FUNCOES DE CONTROLE COM PERSISTENCIA
  // ============================================================================

  const start = useCallback(async () => {
    // Skip if already in progress (avoids redundant API calls)
    if (!monitorRef.current || status === EXECUTION_STATUS.IN_PROGRESS) return;

    monitorRef.current.start();
    setStatus(EXECUTION_STATUS.IN_PROGRESS);

    // Persistir no backend
    if (enablePersistence && planId && planVehicleId) {
      try {
        setIsSyncing(true);
        await routeExecutionService.startExecution(planId, planVehicleId);
        setSyncError(null);
      } catch (error) {
        console.error('[useRouteMonitor] Error persisting start:', error);
        setSyncError(error.message);
      } finally {
        setIsSyncing(false);
      }
    }
  }, [enablePersistence, planId, planVehicleId, status]);

  const stop = useCallback(async () => {
    if (!monitorRef.current || status === EXECUTION_STATUS.COMPLETED) return;

    monitorRef.current.stop();
    setStatus(EXECUTION_STATUS.COMPLETED);

    if (enablePersistence && planId && planVehicleId) {
      try {
        setIsSyncing(true);
        await routeExecutionService.completeExecution(planId, planVehicleId, {
          actualDistance: progress?.distanceTraveled,
          actualDuration: progress?.duration,
        });
        setSyncError(null);
      } catch (error) {
        console.error('[useRouteMonitor] Error persisting stop:', error);
        setSyncError(error.message);
      } finally {
        setIsSyncing(false);
      }
    }
  }, [enablePersistence, planId, planVehicleId, progress, status]);

  const pause = useCallback(async () => {
    if (!monitorRef.current || status !== EXECUTION_STATUS.IN_PROGRESS) return;

    if (enablePersistence && planId && planVehicleId) {
      try {
        setIsSyncing(true);
        const result = await routeExecutionService.pauseExecution(planId, planVehicleId, {
          position: currentPosition,
          currentStopIndex: progress?.currentStopIndex,
          completedStops,
        });
        setSyncError(null);
        // Only update frontend status after backend confirms
        if (!result.alreadyPaused) {
          monitorRef.current.pause();
        }
        setStatus(EXECUTION_STATUS.PAUSED);
      } catch (error) {
        console.error('[useRouteMonitor] Error persisting pause:', error);
        setSyncError(error.message);
      } finally {
        setIsSyncing(false);
      }
    } else {
      monitorRef.current.pause();
      setStatus(EXECUTION_STATUS.PAUSED);
    }
  }, [enablePersistence, planId, planVehicleId, currentPosition, progress, completedStops, status]);

  const resume = useCallback(async () => {
    if (!monitorRef.current || status === EXECUTION_STATUS.IN_PROGRESS) return;

    if (enablePersistence && planId && planVehicleId) {
      try {
        setIsSyncing(true);
        const result = await routeExecutionService.resumeExecution(planId, planVehicleId);
        setSyncError(null);
        // Only update frontend status after backend confirms
        if (!result.alreadyExecuting) {
          monitorRef.current.resume();
        }
        setStatus(EXECUTION_STATUS.IN_PROGRESS);
      } catch (error) {
        console.error('[useRouteMonitor] Error persisting resume:', error);
        setSyncError(error.message);
      } finally {
        setIsSyncing(false);
      }
    } else {
      monitorRef.current.resume();
      setStatus(EXECUTION_STATUS.IN_PROGRESS);
    }
  }, [enablePersistence, planId, planVehicleId, status]);

  const completeStop = useCallback(async (stopId, notes = '', proofData = {}) => {
    if (monitorRef.current) {
      const success = monitorRef.current.completeStop(stopId, notes);
      if (success) {
        setCompletedStops((prev) => [...prev, stopId]);

        // Persistir no backend
        if (enablePersistence && planId) {
          try {
            await routeExecutionService.completeStop(planId, stopId, {
              notes,
              position: currentPosition,
              ...proofData,
            });
          } catch (error) {
            console.error('[useRouteMonitor] Error persisting complete stop:', error);
            setSyncError(error.message);
          }
        }
      }
      return success;
    }
    return false;
  }, [enablePersistence, planId, currentPosition]);

  const skipStop = useCallback(async (stopId, reason = '') => {
    if (monitorRef.current) {
      const success = monitorRef.current.skipStop(stopId, reason);

      // Persistir no backend
      if (success && enablePersistence && planId) {
        try {
          await routeExecutionService.skipStop(planId, stopId, reason);
        } catch (error) {
          console.error('[useRouteMonitor] Error persisting skip stop:', error);
          setSyncError(error.message);
        }
      }

      return success;
    }
    return false;
  }, [enablePersistence, planId]);

  const clearAlert = useCallback((alertId) => {
    setAlerts((prev) => prev.filter((a) => a.id !== alertId));
  }, []);

  const clearAllAlerts = useCallback(() => {
    setAlerts([]);
    if (monitorRef.current) {
      monitorRef.current.clearOldAlerts(0);
    }
  }, []);

  // ============================================================================
  // FUNCOES UTILITARIAS
  // ============================================================================

  // Calcular progresso manualmente (para uso sem o monitor ativo)
  const getProgress = useCallback(() => {
    if (!route || !currentPosition) return null;
    return calculateRouteProgress(currentPosition, route, completedStops.map((id) => ({ id })));
  }, [route, currentPosition, completedStops]);

  // Verificar desvio manualmente
  const checkDeviation = useCallback(() => {
    if (!route?.geometry || !currentPosition) return null;
    return checkRouteDeviation(currentPosition, route.geometry);
  }, [route, currentPosition]);

  // Calcular ETA para proxima parada manualmente
  const getNextStopETA = useCallback(async () => {
    if (!route || !currentPosition) return null;

    const prog = calculateRouteProgress(currentPosition, route, completedStops.map((id) => ({ id })));
    if (!prog.nextStop) return null;

    return calculateETA(currentPosition, prog.nextStop);
  }, [route, currentPosition, completedStops]);

  // Sincronizar manualmente com o backend
  const syncNow = useCallback(async () => {
    if (!planId) return null;

    try {
      setIsSyncing(true);
      const state = await routeExecutionService.getExecutionState(planId);

      if (state) {
        setStatus(mapExecutionStateToStatus(state.executionState));
        setCompletedStops(state.completedStops || []);
        setProgress(state.progress);
      }

      setSyncError(null);
      return state;
    } catch (error) {
      console.error('[useRouteMonitor] Error syncing:', error);
      setSyncError(error.message);
      return null;
    } finally {
      setIsSyncing(false);
    }
  }, [planId]);

  return {
    // Estado
    status,
    progress,
    deviation,
    nextStopETA,
    alerts,
    completedStops,
    currentPosition,
    lastUpdate,
    isActive: status === EXECUTION_STATUS.IN_PROGRESS,
    isComplete: status === EXECUTION_STATUS.COMPLETED,
    isPaused: status === EXECUTION_STATUS.PAUSED,

    // Estado de sincronizacao
    isSyncing,
    syncError,

    // Controles
    start,
    stop,
    pause,
    resume,
    completeStop,
    skipStop,

    // Alertas
    clearAlert,
    clearAllAlerts,

    // Utilitarios
    getProgress,
    checkDeviation,
    getNextStopETA,
    syncNow,

    // Referencia ao monitor (para uso avancado)
    monitor: monitorRef.current,
  };
};

/**
 * Hook simplificado para monitorar multiplas rotas
 */
export const useMultiRouteMonitor = (routes, vehicles) => {
  const monitors = useMemo(() => {
    if (!routes || !vehicles) return [];

    return routes.map((route, index) => {
      const vehicle = vehicles.find((v) => v.id === route.vehicleId) || vehicles[index];
      return { route, vehicle };
    });
  }, [routes, vehicles]);

  // Usar um state compartilhado para todas as rotas
  const [monitorStates, setMonitorStates] = useState({});

  // Atualizar estados quando mudarem
  const updateMonitorState = useCallback((vehicleId, state) => {
    setMonitorStates((prev) => ({
      ...prev,
      [vehicleId]: state,
    }));
  }, []);

  return {
    monitors,
    monitorStates,
    updateMonitorState,
  };
};

/**
 * Hook para acompanhar apenas a posicao e desvio (sem monitoramento completo)
 */
export const useRouteTracking = (route, deviceId) => {
  const positions = useSelector((state) => state.session.positions);
  const position = positions?.[deviceId];

  const [deviation, setDeviation] = useState(null);
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    if (!position || !route) return;

    // Calcular desvio
    if (route.geometry) {
      const dev = checkRouteDeviation(position, route.geometry);
      setDeviation(dev);
    }

    // Calcular progresso
    const prog = calculateRouteProgress(position, route, []);
    setProgress(prog);
  }, [position, route]);

  return {
    position,
    deviation,
    progress,
    isOnRoute: deviation ? !deviation.deviated : true,
  };
};

/**
 * Hook para recuperar e gerenciar estado de execucao salvo
 */
export const useExecutionState = (planId) => {
  const [state, setState] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!planId) {
      setIsLoading(false);
      return;
    }

    const loadState = async () => {
      try {
        setIsLoading(true);
        const executionState = await routeExecutionService.getExecutionState(planId);
        setState(executionState);
        setError(null);
      } catch (err) {
        console.error('[useExecutionState] Error loading state:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    loadState();
  }, [planId]);

  const refresh = useCallback(async () => {
    if (!planId) return;

    try {
      const executionState = await routeExecutionService.getExecutionState(planId);
      setState(executionState);
      setError(null);
    } catch (err) {
      console.error('[useExecutionState] Error refreshing state:', err);
      setError(err.message);
    }
  }, [planId]);

  return {
    state,
    isLoading,
    error,
    refresh,
    isInProgress: state?.executionState === EXECUTION_STATE.IN_PROGRESS,
    isPaused: state?.executionState === EXECUTION_STATE.PAUSED,
    isCompleted: state?.executionState === EXECUTION_STATE.COMPLETED,
  };
};

export default useRouteMonitor;
